import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const FEATURES = [
  { icon: '🧾', text: 'Fast dine-in, takeaway & delivery billing' },
  { icon: '🖨️', text: 'KOT and bill printing with WhatsApp receipts' },
  { icon: '📦', text: 'Live inventory and recipe-based consumption' },
  { icon: '⭐', text: 'Loyalty points, offers and sales reports' },
];

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail]       = useState('');
  const [pass, setPass]         = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // Already signed in — skip the login screen
  useEffect(() => { if (user) nav('/dashboard', { replace: true }); }, [user, nav]);

  // Page title for this screen
  useEffect(() => {
    const prev = document.title;
    document.title = 'Sign in | VIP Pizza Billing';
    return () => { document.title = prev; };
  }, []);

  const handle = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await login(email.trim(), pass); nav('/dashboard'); }
    catch {
      setError('Invalid email or password. Please try again.');
      toast.error('Invalid email or password');
    }
    setLoading(false);
  };

  return (
    <div className="vp-login">
      <style>{`
        .vp-login { min-height:100vh; display:flex; background:#fff; }
        .vp-hero { flex:1.1; background:linear-gradient(145deg,#ED1B24 0%,#d11620 55%,#a80f18 100%);
          color:#fff; padding:56px 60px; display:flex; flex-direction:column; justify-content:space-between; position:relative; overflow:hidden; }
        .vp-brand img { width:260px; max-width:100%; display:block; border-radius:16px; box-shadow:0 10px 30px rgba(0,0,0,.25); }
        .vp-hero h1 { font-size:38px; line-height:1.15; margin:0 0 14px; font-weight:800; }
        .vp-hero p.sub { font-size:16px; opacity:.9; margin:0 0 28px; max-width:440px; }
        .vp-feat { list-style:none; padding:0; margin:0; display:grid; gap:12px; position:relative; z-index:1; }
        .vp-feat li { display:flex; align-items:center; gap:12px; font-size:14.5px; }
        .vp-feat .ic { width:34px; height:34px; border-radius:9px; background:rgba(255,255,255,.18); display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
        .vp-foot { font-size:12px; opacity:.75; position:relative; z-index:1; }
        .vp-form-wrap { flex:1; display:flex; align-items:center; justify-content:center; padding:32px 24px; background:#faf8f6; }
        .vp-card { width:100%; max-width:380px; }
        .vp-card h2 { font-size:26px; margin:0 0 6px; font-weight:800; color:#222; }
        .vp-card .hint { font-size:14px; color:#888; margin:0 0 26px; }
        .vp-pass { position:relative; }
        .vp-pass .toggle { position:absolute; right:10px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; font-size:12px; color:#ED1B24; font-weight:600; padding:4px; }
        .vp-err { background:#fdecea; color:#b3261e; border:1px solid #f5c2bd; border-radius:8px; padding:9px 12px; font-size:13px; margin-bottom:14px; }
        @media (max-width: 820px) {
          .vp-login { flex-direction:column; }
          .vp-hero { flex:none; padding:24px 24px 28px; }
          .vp-brand img { width:170px; }
          .vp-hero h1 { font-size:26px; }
          .vp-hero p.sub { font-size:14px; margin-bottom:0; }
          .vp-feat, .vp-foot { display:none; }
          .vp-hero-mid { margin:22px 0 0; }
        }
      `}</style>

      <div className="vp-hero">
        <div className="vp-brand"><img src={`${process.env.PUBLIC_URL}/vip-logo.png`} alt="VIP Pizza — Serving Sweet Smiles" /></div>
        <div className="vp-hero-mid">
          <h1>Billing & restaurant<br />management, made simple.</h1>
          <p className="sub">Sign in to punch orders, print KOTs, track inventory and see how every outlet is doing.</p>
          <ul className="vp-feat">
            {FEATURES.map(f => (
              <li key={f.text}><span className="ic">{f.icon}</span>{f.text}</li>
            ))}
          </ul>
        </div>
        <div className="vp-foot">© {new Date().getFullYear()} VIP Pizza · Ghanakshi Foods</div>
      </div>

      <div className="vp-form-wrap">
        <div className="vp-card">
          <h2>Welcome back</h2>
          <p className="hint">Sign in to your VIP Pizza Billing account.</p>

          {error && <div className="vp-err" role="alert">{error}</div>}

          <form onSubmit={handle}>
            <div className="field"><label htmlFor="vp-email">Email</label>
              <input id="vp-email" className="input" type="email" autoComplete="username" value={email}
                onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" required autoFocus /></div>
            <div className="field"><label htmlFor="vp-pass">Password</label>
              <div className="vp-pass">
                <input id="vp-pass" className="input" type={showPass?'text':'password'} autoComplete="current-password" value={pass}
                  onChange={e=>setPass(e.target.value)} placeholder="••••••••" required style={{ paddingRight:56 }} />
                <button type="button" className="toggle" onClick={()=>setShowPass(s=>!s)}>{showPass?'Hide':'Show'}</button>
              </div></div>
            <button className="btn btn-primary" type="submit" disabled={loading}
              style={{ width:'100%', padding:11, marginTop:8, fontSize:15, fontWeight:600 }}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
          <p style={{ fontSize:12, color:'#aaa', textAlign:'center', marginTop:22 }}>Forgot your login? Contact your admin for credentials.</p>
        </div>
      </div>
    </div>
  );
}